/**
 * Dual-environment authenticated GET for route-loader prefetching.
 *
 * During SSR, apiClient's `credentials: 'include'` has no cookie jar to draw
 * from, so auth-required loader fetches must go through the cookie-forwarding
 * server fn (browser → SSR node → Go API, two hops). On client-side
 * navigations that proxy is pure latency: the browser can call the Go API
 * directly (browser → Go, one hop) — the same transport every client queryFn
 * (marketApi, getMyFavoritesQueryFn, …) already uses. These helpers pick the
 * transport per environment while keeping ONE unwrapping contract, so a
 * loader-primed cache entry and the component's background refetch land in
 * the identical shape under the same query key.
 */
import { apiClient } from '@/lib/api/client';
import {
  authenticatedGetEnvelopeServer,
  authenticatedGetServer,
} from '@/lib/server-fns/authenticated-get-fns';

/** Unwrapped-payload GET (v2 envelope `data` extracted), SSR or browser. */
export async function authenticatedGetShared<T>(path: string): Promise<T> {
  if (typeof window === 'undefined') {
    return authenticatedGetServer({ data: path }) as Promise<T>;
  }
  const result = await apiClient.get<T>(path);
  if (!result.ok) throw new Error(result.error.message);
  return result.value;
}

/** Full-envelope GET (keeps `pagination` siblings), SSR or browser. */
export async function authenticatedGetEnvelopeShared<T>(path: string): Promise<T> {
  if (typeof window === 'undefined') {
    return authenticatedGetEnvelopeServer({ data: path }) as Promise<T>;
  }
  const result = await apiClient.getRaw<T>(path);
  if (!result.ok) throw new Error(result.error.message);
  return result.value;
}
